import React, { useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  useTheme,
  Fade
} from "@mui/material";
import { ColorModeContext, tokens } from "../../theme";
import PagesEndPoint from "../../utils/constants/PagesEndPoint";
import KeycloakService from "../config/KeycloakService";
import {
  Menu as MenuIcon,
  Moon,
  Sun,
  Bell,
  Settings,
  LogOut,
  User,
  LayoutDashboard,
  Calendar,
  Image,
  PlusCircle,
  ClipboardList,
  Users as UsersIcon,
  FileText,
  Receipt,
  HelpCircle,
  Mail,
  DollarSign
} from 'lucide-react';

const pages = [
  { title: "Dashboard", url: PagesEndPoint.DASHBOARD_PAGE_URL, icon: <LayoutDashboard size={18} /> },
  { title: "Events", url: PagesEndPoint.EVENT_PAGE_URL, icon: <Calendar size={18} /> },
  { title: "Gallery", url: PagesEndPoint.GALLERY_PAGE_URL, icon: <Image size={18} /> },
  { title: "AddOns", url: PagesEndPoint.ADDONS_PAGE_URL, icon: <PlusCircle size={18} /> },
  { title: "Pricing", url: PagesEndPoint.RATE_CONFIG_PAGE_URL, icon: <DollarSign size={18} /> },
  { title: "Appointments", url: PagesEndPoint.APPOINTMENTS_PAGE_URL, icon: <ClipboardList size={18} /> },
  { title: "Reservations", url: PagesEndPoint.RESERVATIONS_PAGE_URL, icon: <FileText size={18} /> },
  { title: "Users", url: PagesEndPoint.USERS_PAGE_URL, icon: <UsersIcon size={18} /> },
  { title: "Invoices", url: PagesEndPoint.INVOICES_PAGE_URL, icon: <Receipt size={18} /> },
  { title: "Receipts", url: PagesEndPoint.RECEIPTS_PAGE_URL, icon: <FileText size={18} /> },
  { title: "Inquiries", url: PagesEndPoint.INQUIRIES_PAGE_URL, icon: <Mail size={18} /> },
  { title: "FAQs", url: PagesEndPoint.FAQS_PAGE_URL, icon: <HelpCircle size={18} /> },
];

const settings = [
  { title: "Profile", url: PagesEndPoint.PROFILE_PAGE_URL, icon: <User size={18} /> },
  { title: "Account", url: PagesEndPoint.ACCOUNT_PAGE_URL, icon: <Settings size={18} /> },
  { title: "Logout", icon: <LogOut size={18} /> },
];

const ResponsiveAppBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const navigateTo = useNavigate();
  const location = useLocation();

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const closeMenuAndNavigateTo = (path, handleMenuClose) => {
    handleMenuClose();
    navigateTo(path);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: theme.palette.mode === "dark"
          ? "rgba(31, 31, 31, 0.8)"
          : "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(12px)",
        backgroundImage: "none",
        boxShadow: "none",
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            height: "70px",
            display: "flex",
            justifyContent: "space-between"
          }}
        >
          {/* Logo Section */}
          <Box display="flex" alignItems="center">
            <IconButton
              onClick={() => navigateTo(PagesEndPoint.DASHBOARD_PAGE_URL)}
              sx={{ p: 0, mr: 2 }}
            >
              <Avatar
                src="le_prestige_hall_logo.png"
                alt="Logo"
                sx={{
                  width: 44,
                  height: 44,
                  transition: "transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  "&:hover": { transform: "rotate(10deg) scale(1.1)" }
                }}
              />
            </IconButton>
            <Typography
              variant="h4"
              noWrap
              sx={{
                display: { xs: "none", lg: "flex" },
                fontFamily: "Josefin Sans",
                fontWeight: 700,
                letterSpacing: ".1rem",
                color: colors.grey[100],
                textDecoration: "none",
              }}
            >
              ADMIN<span style={{ color: colors.greenAccent[500] }}>ZONE</span>
            </Typography>
          </Box>

          {/* Core Navigation (Desktop) */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: "4px",
              bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
              p: "4px",
              borderRadius: "14px",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`
            }}
          >
            {pages.slice(0, 3).map((page) => (
              <Button
                key={page.title}
                onClick={() => closeMenuAndNavigateTo(page.url, handleCloseNavMenu)}
                sx={{
                  px: "16px",
                  py: "8px",
                  borderRadius: "10px",
                  color: isActive(page.url)
                    ? (theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100])
                    : colors.grey[100],
                  bgcolor: isActive(page.url) ? colors.greenAccent[500] : "transparent",
                  textTransform: "none",
                  fontWeight: "600",
                  fontFamily: "Josefin Sans",
                  display: "flex",
                  gap: "8px",
                  "&:hover": {
                    bgcolor: isActive(page.url) ? colors.greenAccent[400] : "rgba(255,255,255,0.05)",
                    transform: "translateY(-1px)"
                  },
                  transition: "all 0.2s"
                }}
              >
                {page.icon}
                {page.title}
              </Button>
            ))}
            <IconButton
              onClick={handleOpenNavMenu}
              sx={{
                color: colors.grey[100],
                borderRadius: "10px",
                "&:hover": { bgcolor: "rgba(255,255,255,0.05)" }
              }}
            >
              <MenuIcon size={20} />
            </IconButton>
          </Box>

          {/* Right Actions Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              onClick={colorMode.toggleColorMode}
              sx={{
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                p: "10px",
                borderRadius: "12px",
                color: colors.greenAccent[500],
                "&:hover": { transform: "rotate(45deg)", bgcolor: "rgba(255,255,255,0.08)" },
                transition: "all 0.3s"
              }}
            >
              {theme.palette.mode === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </IconButton>

            <IconButton
              sx={{
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                p: "10px",
                borderRadius: "12px",
                color: colors.grey[100],
                "&:hover": { bgcolor: "rgba(255,255,255,0.08)" }
              }}
            >
              <Bell size={20} />
            </IconButton>

            <Tooltip title="Account Settings" TransitionComponent={Fade}>
              <IconButton
                onClick={handleOpenUserMenu}
                sx={{
                  p: "4px",
                  border: `2px solid ${colors.greenAccent[500]}`,
                  ml: 1,
                  transition: "all 0.2s",
                  "&:hover": { transform: "scale(1.05)" }
                }}
              >
                <Avatar
                  alt={`${KeycloakService.getUserFirstName()} ${KeycloakService.getUserLastName()}`}
                  src="/static/images/avatar/2.jpg"
                  sx={{ width: 32, height: 32 }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Large Menu for remaining pages */}
          <Menu
            anchorEl={anchorElNav}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            TransitionComponent={Fade}
            sx={{
              mt: "15px",
              "& .MuiPaper-root": {
                borderRadius: "20px",
                minWidth: "220px",
                bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : "#FFFFFF",
                boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                p: "8px",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`
              },
            }}
          >
            {pages.map((page) => (
              <MenuItem
                key={page.title}
                onClick={() => closeMenuAndNavigateTo(page.url, handleCloseNavMenu)}
                sx={{
                  borderRadius: "12px",
                  py: "12px",
                  px: "16px",
                  display: "flex",
                  gap: "12px",
                  color: isActive(page.url) ? colors.greenAccent[500] : colors.grey[100],
                  "&:hover": { bgcolor: "rgba(255,255,255,0.05)" }
                }}
              >
                <Box sx={{ color: colors.greenAccent[500] }}>{page.icon}</Box>
                <Typography sx={{ fontWeight: isActive(page.url) ? "700" : "500", fontFamily: "Josefin Sans" }}>
                  {page.title}
                </Typography>
              </MenuItem>
            ))}
          </Menu>

          {/* User Settings Menu */}
          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            TransitionComponent={Fade}
            sx={{
              mt: "15px",
              "& .MuiPaper-root": {
                borderRadius: "20px",
                minWidth: "200px",
                bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : "#FFFFFF",
                boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                p: "8px",
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, mb: 1, borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}` }}>
              <Typography variant="subtitle2" fontWeight="bold" color={colors.grey[100]}>
                {KeycloakService.getUserFirstName()} {KeycloakService.getUserLastName()}
              </Typography>
              <Typography variant="caption" color={colors.grey[400]}>
                Administrator
              </Typography>
            </Box>

            {KeycloakService.isLoggedIn ? (
              settings.map((setting) => (
                <MenuItem
                  key={setting.title}
                  onClick={() => setting.title === "Logout" ? KeycloakService.doLogout() : closeMenuAndNavigateTo(setting.url, handleCloseUserMenu)}
                  sx={{
                    borderRadius: "12px",
                    py: "10px",
                    gap: "12px",
                    color: setting.title === "Logout" ? colors.redAccent[500] : colors.grey[100],
                    "&:hover": { bgcolor: setting.title === "Logout" ? `${colors.redAccent[500]}1A` : "rgba(255,255,255,0.05)" }
                  }}
                >
                  {setting.icon}
                  <Typography variant="body2" sx={{ fontWeight: "600", fontFamily: "Josefin Sans" }}>{setting.title}</Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem onClick={KeycloakService.doLogin}>
                <Typography textAlign="center">Log In</Typography>
              </MenuItem>
            )}
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default ResponsiveAppBar;
